import * as React from 'react';
import LocalDiv from '../../localization/LocalDiv';
import { css } from '@emotion/core';
import { FLEX_COLUMN } from '../../layout/flexBox';
//TODO - at some point, delete '../assets/logo.svg';
import truScoreLogo from '../../assets/logo_tbc.svg';
import hrdqLogo from '../../assets/logo_hrdq.gif';
import zfLogo from '../../assets/logo_zf.png';
import talentSmartLogo from '../../assets/logo_talentsmart.png';
import { COLORS } from '../../globalStyle/colors';
import Msg from '../../localization/Msg';
import { Str } from '../../localization/translationKeys';
import { useRoutes } from '../../navigation/routes';
import { useAppState } from '../../core/redux/hooks';
import AppActions from './AppActions';
import ProjectSteps from './ProjectSteps';
import ResourceMenu from './ResourceMenu';
import { SELECT } from '../../forms/styles/select';
import { addImgEncoding } from '../../language/module';
import { useBrowser } from '../../language/useBrowser';
import LocalLink from '../../localization/LocalLink';
import { useProjIds } from '../../navigation/hooks';
import Spacer from '../../layout/Spacer';


const styles = {
  root:             css`
    align-items: stretch;
    padding: 0 10px 15px;
    height: 100%;
    box-sizing: border-box;

    overflow-y: scroll;
    -ms-overflow-style: none; // IE-specific
    scrollbar-width: none; // Firefox-specific

    ::-webkit-scrollbar {
      width: 0;
      background: transparent;
    }
  `,
  logo:             css`
    width: 184px;
    margin: 20px auto; 
    padding: 0;
  `,
  projectSelect:    css`
    height: 48px;
    flex-basis: 48px;
    flex-shrink: 0;
    margin: 0 -10px;
    border-radius: 0;
    border-color: ${COLORS.LIGHTER_GREY};
    background-color: transparent;
    text-decoration: none;
  `,
  mockSelectDiv:    css`
    border-top: 0 none;
    justify-content: center;
    padding: 0 15px;
    border-bottom: 1px solid ${COLORS.LIGHTER_GREY};
    border-right: 1px solid ${COLORS.LIGHTER_GREY};
  `,
  mockSelectSpan:   css`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  realSelect:       css`
    padding: 0 20px;
    
    &:hover { 
      color: ${COLORS.BLUE};
    }
  `,
  spacer:           css`
    flex-basis: 30px;
    flex-shrink: 0;
  `,
  projectResources: css`
    margin-bottom: 60px;
  `,
  appActions:       css`
    margin-top: auto;
    margin-bottom: 15px;
  `,
  footer:           css`
    font-size: 11.5px;
    color: ${COLORS.MEDIUM_GREY};
    
    & > a { 
      text-decoration: none;
      color: ${COLORS.BLUE}
    }
    
    & > a:hover { 
      text-decoration: underline;
    }
    
    @supports (-webkit-touch-callout: none) {
      margin-bottom: 60px;
    }
  `,
};


type Props = {
  className?: string;
};


/**
 * ProjectMenu: a component that renders the contents of the side menu on project pages -- survey steps, project name
 * etc.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ProjectMenu: React.FC<Props> = props => {
  const { onIe } = useBrowser();

  const projIds   = useProjIds();
  const projects  = useAppState(state => state.projects || []);
  const project   = useAppState(state => state.projectSummary);
  const resources = useAppState(state => state.projectResources || []);

  const routes = useRoutes(projIds.participantId, projIds.projDisplayId);

  const config = useAppState(state => state.config);
  const system = (config?.system || '').toLowerCase();
  const defaultLogo =  React.useMemo (
    () => system === 'zf'
            ? zfLogo
            : system === 'hrdq'
              ? hrdqLogo
              : system === 'talentsmart'
                ? talentSmartLogo
                : truScoreLogo // always default to TBC
    ,
    [system],
  );

  const projectLogo = React.useMemo(
    () => project?.clientImageData ? addImgEncoding(project.clientImageData) : defaultLogo,
    [project, defaultLogo],
  );

  if (!project) {
    return <div className={props.className} />;
  }

  return (
    <LocalDiv css={[FLEX_COLUMN, styles.root]} className={props.className}>
      { // Survey Select
        projects.length > 1
          ? (
            <LocalLink
              to={routes.SelectProject}
              css={[SELECT, styles.projectSelect, FLEX_COLUMN, styles.mockSelectDiv, styles.realSelect]}
            >
              <span css={styles.mockSelectSpan}>{project?.surveyName || projects[0]?.surveyName}</span>
            </LocalLink>
          )
          : (
            <div css={[styles.projectSelect, FLEX_COLUMN, styles.mockSelectDiv]}>
              <span css={styles.mockSelectSpan}>{project?.surveyName || projects[0]?.surveyName}</span>
            </div>
          )
      }

      {
        /* Project Logo */
        onIe
          ? (
            <div css={[styles.logo]}>
              <img src={projectLogo} alt='Project Logo' />
            </div>
          )
          : (
            <img src={projectLogo} alt='Project Logo' css={[styles.logo]} />
          )
      }

      {/* Links */}
      <ProjectSteps routes={routes} proj={project} participantId={projIds.participantId} />

      {/* Spacer */}
      <Spacer sizePx={30} css={styles.spacer}/>

      { // Resources
        !!resources?.length && (
          <ResourceMenu
            resources={resources}
            routes={routes}
            css={styles.projectResources}
          />
        )
      }

      {/* Options */}
      <AppActions
        css={styles.appActions}
        loginUrl={routes.Login}
        accountUrl={routes.Account}
        languageUrl={routes.Language}
        customerServiceUrl={routes.CustomerService}
      />

      {/* Footer: Copyright/Privacy Policy  */}
      <Msg defaultMsg='' str={Str.FooterCopyrightPrivacy} css={styles.footer} />
    </LocalDiv>
  );
};

export default ProjectMenu;
